import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useController, useFieldArray, useForm } from 'react-hook-form';
import { useMutation, useSubscription } from '@apollo/client';
import { CONVERSATIONS_QUERY } from './queries';
import { CREATE_CASE_CHAT, UPDATE_CASE_CHAT } from './mutations';
import { CREATE_CASE_CHAT_SUBSCRIPTION, UPDATE_CASE_CHAT_SUBSCRIPTION } from './subscriptions';
import { SectionContext } from './context';
import ClientInquiryPanel from './ClientInquiryPanel';
import MessageTextEditor from './MessageTextEditor';
import RichText from './shared/RichText';
import MessageSectionTag from './shared/MessageSectionTag';
import { formatBytes, pluralize, scrollToBottom, showElement, uploadFile } from './shared/helperFunctions';
import { getCaseIdFromUrl } from './hooks';

const MessageForm = ({
    parentChat,
    setParentChat,
    isInquiry,
    setIsInquiry,
    caseIsClientInquiry,
    dispatchUploads
}) => {
    const editorRef = useRef(null);
    const { activeSection } = useContext(SectionContext) || {};
    const [error, setError] = useState();
    let params = useParams();
    let caseId = getCaseIdFromUrl();

    // If active section is set, set as value of section tag
    useEffect(() => {
        const { id, key } = activeSection || {};
        if (key) setValue('sectionOption', { id: id || key, caseNetworkId: params.networkId });
    }, [activeSection]);

    // If parent chat is set, set section of parent chat as value of section tag
    useEffect(() => {
        const { section, caseNetworkId } = parentChat || {};
        if (section) {
            setValue('sectionOption', { id: caseNetworkId || section, caseNetworkId });
        }
    }, [parentChat]);

    const { register, control, setValue, handleSubmit, resetField } = useForm();
    const { field: content } = useController({
        control,
        name: 'content',
        defaultValue: '',
    });
    const { fields, append, remove, replace } = useFieldArray({
        control,
        name: 'attachments',
        keyName: 'key'
    });

    const [createCaseChat, { loading }] = useMutation(CREATE_CASE_CHAT, {
        onCompleted: () => handleSuccess(),
        onError: (error) => handleError(error),
    });
    const [updateCaseChat] = useMutation(UPDATE_CASE_CHAT);
    useSubscription(CREATE_CASE_CHAT_SUBSCRIPTION, {
        variables: { caseId },
        onSubscriptionData: ({ client: { cache }, subscriptionData: { data } }) => {
            if (data.createCaseChat) {
                updateCache(cache, caseId, data.createCaseChat);
                handleSubscriptionData(data.createCaseChat);
            }
        }
    });
    useSubscription(UPDATE_CASE_CHAT_SUBSCRIPTION, {
        variables: { caseId },
    });

    const onSubmit = async ({ content, sectionOption, attachments, isClientInquiry }) => {
        var variables = {
            params: {
                caseId,
                content,
                parentCaseChatId: parentChat?.id,
                isClientInquiry: caseIsClientInquiry ? isClientInquiry : false,
                ...sectionParams(sectionOption),
            }
        }
        await createCaseChat({ variables })
            .then(({ data: { createCaseChat: { caseChat } } }) => {
                handleUpload(caseChat.id, attachments);
            });
    }

    const handleSuccess = () => {
        setParentChat(null);
        resetField('content');
        resetField('sectionOption');
        replace([]);
        setError(null);
    }

    const handleError = (error) => {
        setError(error);
        throw error;
    }

    const handleSubscriptionData = (data) => {
        const { caseChat } = data;
        if (!caseChat.isAuthor) {
            showElement('#js-messagesNotificationDot');
        }
        setTimeout(() => scrollToBottom('#js-messagesContainer'), 100);
    }

    const handleDrop = (e) => {
        e.preventDefault();
        Array.from(e.dataTransfer.files).forEach(file => {
            // Compatibility with TinyMCE
            if (!(file instanceof File)) {
                Object.setPrototypeOf(file, File.prototype);
            }
            append({ file });
        });
    }

    const handleAttach = (e) => {
        Array.from(e.target.files).forEach(file => {
            append({ file });
        });
    }

    const handleUpload = (chatId, attachments) => {
        const uploads = attachments.map(attachment => (
            {
                file: attachment.file,
                objectId: chatId,
                attribute: 'attachment',
                properties: { chatId },
                updateObject: updateCaseChat,
            }
        ));
        uploads.forEach(upload => uploadFile(upload, dispatchUploads));
    }

    const canSend = validateContent(editorRef.current) || fields.length;

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="d-flex align-items-center mb-3">
                <button
                    type="button"
                    className={`btn btn-sm rounded-0 rounded-start py-1 btn-${!isInquiry ? 'secondary' : 'outline-secondary'}`}
                    onClick={() => setIsInquiry(false)}
                >
                    <div className="d-flex align-items-center gap-2">
                        <i className="fe fe-message-square" />
                        <span>Chat</span>
                    </div>
                </button>
                <button
                    type="button"
                    className={`btn btn-sm rounded-0 rounded-end py-1 btn-${isInquiry ? 'secondary' : 'outline-secondary'}`}
                    onClick={() => setIsInquiry(true)}
                >
                    <div className="d-flex align-items-center gap-2">
                        <i className="fe fe-help-circle" />
                        <span>Client inquiry</span>
                    </div>
                </button>
                {!isInquiry && (
                    <MessageSectionTag control={control} />
                )}
            </div>
            <div
                className={`border border-secondary-soft rounded ${isInquiry ? 'd-none' : ''}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e)}
            >
                {!!parentChat && (
                    <Reply
                        message={parentChat}
                        setParentChat={setParentChat}
                    />
                )}
                {!!fields.length && (
                    <div className="d-flex gap-3 flex-wrap pt-3 px-3">
                        {fields.map((field, index) => (
                            <Attachment
                                key={field.key}
                                file={field.file}
                                remove={() => remove(index)}
                            />
                        ))}
                    </div>
                )}
                <div className="tinymce-border-0 mb-3">
                    <MessageTextEditor
                        editorRef={editorRef}
                        value={content.value}
                        onChange={content.onChange}
                        onDrop={handleDrop}
                    />
                </div>
                <div className="d-flex align-items-center gap-3 px-3 pb-3">
                    <label className="btn p-2 lh-1 bg-whitesmoke">
                        <i className="fe fe-paperclip fs-3" />
                        <input
                            className="d-none"
                            type="file"
                            multiple={true}
                            onChange={(e) => handleAttach(e)}
                        />
                    </label>
                    {caseIsClientInquiry && (
                        <>
                            <div className="border pt-4" />
                            <div className="form-check">
                                <label className="form-check-label small">
                                    This is a CI-related message
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        defaultChecked={true}
                                        {...register("isClientInquiry")}
                                    />
                                </label>
                            </div>
                        </>
                    )}
                    <button
                        type="submit"
                        className="btn btn-sm btn-dark ms-auto"
                        disabled={!canSend || loading}
                    >
                        Send
                    </button>
                </div>
            </div>
            {!!error && !isInquiry && (
                <div className="text-danger small">
                    {error.message}
                </div>
            )}
            {isInquiry && <ClientInquiryPanel />}
        </form>
    );
}

const Reply = ({ message, message: { author, attachments }, setParentChat }) => {

    return (
        <div className="border-start border-primary border-3 ps-3 mt-3 mx-3">
            <div className="d-flex align-items-center justify-content-between">
                <div className="col-10">
                    <div className="h5 text-break mb-2">
                        Reply to {author.fullname}
                    </div>
                    {!!attachments.length && (
                        <AttachmentsCount attachments={attachments} />
                    )}
                    <div className="line-clamp small">
                        <RichText content={message.content} />
                    </div>
                </div>
                <button
                    type="button"
                    className="btn p-2 lh-1 bg-whitesmoke"
                    onClick={() => setParentChat(null)}
                >
                    <i className="fe fe-x" />
                </button>
            </div>
        </div>
    );
}

const Attachment = ({ file: { name, size }, remove }) => {

    return (
        <div className="badge bg-primary-soft text-dark">
            <div className="d-flex align-items-center gap-2">
                <div className="d-flex align-items-baseline gap-1">
                    <div className="text-truncate lh-base" style={{ maxWidth: '200px' }}>
                        {name}
                    </div>
                    <span>{`(${formatBytes(size)})`}</span>
                </div>
                <button
                    className="btn p-0 lh-1"
                    type="button"
                    onClick={remove}
                >
                    <i className="fe fe-x" />
                </button>
            </div>
        </div>
    );
}

const AttachmentsCount = ({ attachments }) => {

    return (
        <div className="d-flex align-items-center gap-2 small mb-2">
            <i className="fe fe-paperclip" />
            <span className="fst-italic">
                {pluralize(attachments.length, 'attachment')}
            </span>
        </div>
    );
}

function updateCache(cache, caseId, result) {
    const { caseChat, agentLog } = result;

    const query = {
        query: CONVERSATIONS_QUERY,
        variables: {
            caseId,
        },
    }
    const data = cache.readQuery(query);

    if (data) {
        cache.writeQuery({
            ...query,
            data: {
                caseChats: caseChat ? [...data.caseChats, caseChat] : data.caseChats,
                agentLogs: agentLog ? [...data.agentLogs, agentLog] : data.agentLogs,
            }
        });
    }

    if (caseChat) {
        cache.modify({
            id: `Case:${caseId}`,
            fields: {
                chatsCount(cachedChatsCount = 0) {
                    return cachedChatsCount + 1;
                }
            }
        });
    }
}

function validateContent(editor) {
    if (!editor) return false;

    var content = editor.getContent({ format : 'text' });
    return !!content.trim();
}

function sectionParams(option) {
    if (!option) return {};
    return {
        section: option.caseNetworkId ? 'case_network' : option.id,
        caseNetworkId: option.caseNetworkId,
    }
}

export default MessageForm;